/**
 * Google Analytics 4 — Consent-gated integration.
 *
 * GA4 scripts are ONLY injected into the page after the user accepts cookies.
 * On subsequent visits (where consent is already stored), GA4 loads immediately.
 *
 * ─── SETUP ───
 * Replace the GA_MEASUREMENT_ID below with your actual GA4 Measurement ID.
 * You can find it in: Google Analytics → Admin → Data Streams → your stream → Measurement ID
 */

const GA_MEASUREMENT_ID = "G-ZHD0B8J8BX"; // ← Replace with your real GA4 ID

let isInitialized = false;

/**
 * Dynamically injects the GA4 gtag.js script and initializes tracking.
 * Called only when the user accepts cookies (or on load if already accepted).
 */
export function initGA4() {
  if (isInitialized) return;
  if (typeof window === "undefined") return;

  // 1. Create the gtag.js script element
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // 2. Initialize the gtag dataLayer
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = gtag;

  gtag("js", new Date());
  gtag("config", GA_MEASUREMENT_ID, {
    send_page_view: false, // We handle page views manually for SPA routing
  });

  isInitialized = true;
}

/**
 * Sends a page_view event to GA4.
 * Call this on every route change in your React Router setup.
 *
 * @param {string} path — The current page path (e.g. "/product/raptor")
 * @param {string} [title] — Optional page title
 */
export function trackPageView(path, title) {
  if (!isInitialized || typeof window.gtag !== "function") return;

  window.gtag("event", "page_view", {
    page_path: path,
    page_title: title || document.title,
    page_location: window.location.href,
  });
}

/**
 * Sends a custom event to GA4.
 *
 * @param {string} eventName — Event name (e.g. "contact_form_open", "demo_request")
 * @param {Object} [params] — Optional event parameters
 */
export function trackEvent(eventName, params = {}) {
  if (!isInitialized || typeof window.gtag !== "function") return;

  window.gtag("event", eventName, params);
}

/**
 * Checks if GA4 has been initialized (i.e. user accepted cookies).
 */
export function isGA4Active() {
  return isInitialized;
}
